"use client";

import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  useDeleteGoogleDriveFile,
  useUploadGoogleDriveFile,
  type GoogleDriveFile,
} from "@/lib/hooks/google-drive/use-google-drive-browser";
import { downloadDriveFile } from "../utils/download-utils";
import { getDefaultExportFormat } from "../utils/export-formats";

interface UseGoogleDriveBulkActionsProps {
  isSecondAccount: boolean;
  accessToken: string | undefined;
  parentId?: string;
  driveId?: string;
  /** Called after a successful upload/delete so the caller can refresh the list. */
  onAfterChange?: () => void;
}

/**
 * Multi-select + drag-drop helpers for the Drive browser:
 *  - selection set management
 *  - bulk download (sequential, skips folders / undownloadable)
 *  - bulk delete → Trash (reversible)
 *  - drag-drop upload to the current folder
 */
export function useGoogleDriveBulkActions({
  isSecondAccount,
  accessToken,
  parentId,
  driveId,
  onAfterChange,
}: UseGoogleDriveBulkActionsProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDownloadingBulk, setIsDownloadingBulk] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const uploadMutation = useUploadGoogleDriveFile();
  const deleteMutation = useDeleteGoogleDriveFile();

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const downloadSelected = useCallback(
    async (files: GoogleDriveFile[]) => {
      const targets = files.filter(
        (f) => selectedIds.has(f.id) && !f.isFolder && (f.canDownload || f.isGoogleDoc)
      );
      if (targets.length === 0) {
        toast.error("No downloadable files selected");
        return;
      }
      setIsDownloadingBulk(true);
      let ok = 0;
      try {
        for (const file of targets) {
          try {
            const resolvedFormat = file.isGoogleDoc
              ? getDefaultExportFormat(file.mimeType)
              : undefined;
            const ext = resolvedFormat ? `.${resolvedFormat}` : null;
            const fileName =
              file.isGoogleDoc && ext ? file.name.replace(/\.[^/.]+$/, "") + ext : file.name;
            await downloadDriveFile(file.id, fileName, accessToken || "", isSecondAccount, resolvedFormat);
            ok += 1;
          } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            toast.error(`Failed to download ${file.name}: ${message}`);
          }
        }
        if (ok > 0) toast.success(`Downloaded ${ok} file${ok === 1 ? "" : "s"}`);
      } finally {
        setIsDownloadingBulk(false);
      }
    },
    [selectedIds, accessToken, isSecondAccount]
  );

  const deleteSelected = useCallback(
    async (files: GoogleDriveFile[]) => {
      const targets = files.filter(
        (f) => selectedIds.has(f.id) && f.capabilities?.canDelete !== false
      );
      if (targets.length === 0) {
        toast.error("No deletable items selected");
        return;
      }
      setIsDeletingBulk(true);
      let ok = 0;
      try {
        for (const file of targets) {
          try {
            await deleteMutation.mutateAsync({
              fileId: file.id,
              parentId,
              driveId,
              isSecondAccount,
            });
            ok += 1;
          } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            toast.error(`Failed to delete ${file.name}: ${message}`);
          }
        }
        if (ok > 0) {
          toast.success(`Moved ${ok} item${ok === 1 ? "" : "s"} to Trash`);
          clearSelection();
          onAfterChange?.();
        }
      } finally {
        setIsDeletingBulk(false);
      }
    },
    [selectedIds, deleteMutation, parentId, driveId, isSecondAccount, clearSelection, onAfterChange]
  );

  const uploadFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      if (files.length === 0) return;
      setIsUploading(true);
      let ok = 0;
      try {
        for (const file of files) {
          try {
            await uploadMutation.mutateAsync({ file, parentId, driveId, isSecondAccount });
            ok += 1;
          } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            toast.error(`Failed to upload ${file.name}: ${message}`);
          }
        }
        if (ok > 0) {
          toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
          onAfterChange?.();
        }
      } finally {
        setIsUploading(false);
      }
    },
    [uploadMutation, parentId, driveId, isSecondAccount, onAfterChange]
  );

  return {
    selectedIds,
    toggleSelect,
    clearSelection,
    downloadSelected,
    deleteSelected,
    uploadFiles,
    isDownloadingBulk,
    isDeletingBulk,
    isUploading,
  };
}
